import ClientTabHeading from "../clients/ClientTabHeading";
import PageHeading from "../../components/PageHeading";
import SearchPending from "../../components/SearchPending";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { createListSearch } from "../../utils/listSearch";
import { apiErrorMessage } from "../../utils/apiError";
import { useEffect, useState } from "react";
import { Search, Loader2, Eye, Download } from "lucide-react";
import { Modal, message } from "antd";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import axios from "../../axios/AxiosInstance";
import { contractDate } from "../../utils/planningDates";

const displayDate = (value) =>
  contractDate(value)?.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) || "—";
export function monthlyRegisters(missions, month) {
  const [year, number] = month.split("-").map(Number);
  const first = new Date(year, number - 1, 1);
  const last = new Date(year, number, 0);
  const grouped = new Map();
  missions.forEach((mission) => {
    const start = contractDate(mission.DateDebut);
    const end =
      contractDate(mission.DateFinMission || mission.DateFin) || start;
    if (!start || !end || start > last || end < first || end < start) return;
    const name = mission.RaisonSociale || "Client sans nom";
    const id = String(mission.IdClient || name);
    if (!grouped.has(id)) grouped.set(id, { id, name, month, missions: [] });
    grouped.get(id).missions.push(mission);
  });
  return [...grouped.values()].sort((a, b) =>
    a.name.localeCompare(b.name, "fr"),
  );
}
function registerPdf(row, label) {
  const pdf = new jsPDF({ orientation: "landscape" });
  pdf.setFontSize(18);
  pdf.text("J’M INTERIM — Registre du personnel", 14, 18);
  pdf.setFontSize(11);
  pdf.text(pdf.splitTextToSize(`${row.name} · ${label}`, 265), 14, 28);
  autoTable(pdf, {
    startY: 44,
    head: [["Intérimaire", "Contrat", "Début de mission", "Fin de mission"]],
    body: row.missions.map((m) => [
      m.NomPrenom || "—",
      String(m.NumeroContrat || "—"),
      displayDate(m.DateDebut),
      displayDate(m.DateFinMission || m.DateFin),
    ]),
    styles: { fontSize: 10, cellPadding: 4 },
    headStyles: { fillColor: [30, 64, 175] },
    margin: { bottom: 22 },
    didDrawPage: () => {
      pdf.setFontSize(8);
      pdf.setTextColor(100);
      pdf.text(
        "Export des contrats disponibles pour ce mois. Ce document ne remplace pas le registre fourni par l’agence.",
        14,
        pdf.internal.pageSize.getHeight() - 10,
      );
    },
  });
  return pdf;
}
export function clientRegisterMissions(missions, client) {
  if (!client) return missions;
  return missions.filter((mission) => {
    const id =
      mission.client?._id ||
      mission.clientId ||
      (typeof mission.client === "string" ? mission.client : null);
    if (id && client._id) return String(id) === String(client._id);
    if (
      mission.IdClient != null &&
      client.pld != null &&
      String(client.pld).trim()
    )
      return String(mission.IdClient) === String(client.pld);
    const name = String(client.companyName || client.denomination || "")
      .trim()
      .toLocaleLowerCase("fr-FR");
    return (
      Boolean(name) &&
      String(mission.RaisonSociale || "")
        .trim()
        .toLocaleLowerCase("fr-FR") === name
    );
  });
}
export default function RegistrePersonnel({ client, embedded = false }) {
  const [missions, setMissions] = useState([]);
  const [search, setSearch] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(search);
  const [month, setMonth] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    axios
      .get("pld-contract/freelance", { signal: controller.signal })
      .then(({ data }) => {
        if (active) setMissions(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        const detail = apiErrorMessage(error, "Impossible de charger les contrats. Actualisez la page pour réessayer.");
        if (active && detail) setError(detail);
      })
      .finally(() => {
        if (active && !controller.signal.aborted) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, []);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview.url);
    },
    [preview],
  );
  const label = new Date(`${month}-01T12:00:00`).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
  const rows = monthlyRegisters(
    clientRegisterMissions(missions, client),
    month,
  ).filter(createListSearch(appliedSearch, ["name", "month"]));
  const openDocument = (row, download) => {
    try {
      const pdf = registerPdf(row, label);
      if (download)
        pdf.save(
          `Registre_${row.name.replace(/[^\p{L}\p{N}-]/gu, "_")}_${month}.pdf`,
        );
      else
        setPreview({
          title: `${row.name} · ${label}`,
          url: URL.createObjectURL(pdf.output("blob")),
        });
    } catch (error) {
      message.error(apiErrorMessage(error, "Impossible de générer le document. Réessayez."));
    }
  };
  return (
    <div className="">
      <div className="mb-6 flex items-center gap-3">
        <div>
          {embedded ? (
            <ClientTabHeading title="Registre du personnel" description="Consultez les intérimaires ayant travaillé pour ce client." />
          ) : (
            <PageHeading
              icon="register"
              title={<> Registre du personnel </>}
              description={
                <>
                  {" "}
                  Consultez et téléchargez le document mensuel de chaque
                  client.{" "}
                </>
              }
            />
          )}
        </div>
      </div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        {!embedded && (
          <div className="relative w-full max-w-md">
            <Search
              size={18}
              aria-hidden="true"
              className="absolute left-3 top-3 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Rechercher un client"
              placeholder="Nom du client…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm"
            />
            <div className="mt-2"><SearchPending pending={searching} /></div>
          </div>
        )}
        <label className="text-sm text-slate-600">
          Mois du registre
          <input
            type="month"
            value={month}
            onChange={(e) => {
              if (/^\d{4}-\d{2}$/.test(e.target.value))
                setMonth(e.target.value);
            }}
            className="mt-1 block rounded-lg border border-slate-200 bg-white px-3 py-2"
          />
        </label>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Documents générés à partir des contrats disponibles. Les fichiers de
        registre de l’agence ne sont pas encore raccordés.
      </p>
      {loading ? (
        <div
          role="status"
          className="flex justify-center gap-3 py-16 text-slate-500"
        >
          <Loader2 className="animate-spin" />
          Chargement…
        </div>
      ) : error ? (
        <p role="alert" className="rounded-xl bg-rose-50 p-5 text-rose-700">
          {error}
        </p>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {["Nom du client", "Mois du registre", "Actions"].map(
                  (title) => (
                    <th
                      key={title}
                      scope="col"
                      className={
                        ["Actions", "Mois du registre"].includes(title)
                          ? "text-center"
                          : undefined
                      }
                    >
                      {title}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="font-semibold text-slate-900">{row.name}</td>
                  <td className="capitalize text-center">{label}</td>
                  <td className="text-center">
                    <div className="flex flex-wrap justify-center gap-2">
                      <button
                        onClick={() => openDocument(row, false)}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-blue-700 hover:bg-blue-50"
                      >
                        <Eye size={16} />
                        Visualiser
                      </button>
                      <button
                        onClick={() => openDocument(row, true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-white hover:bg-blue-800"
                      >
                        <Download size={16} />
                        Télécharger
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={3} className="py-10 text-center text-slate-500">
                    Aucun document pour ce mois et cette recherche.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        open={Boolean(preview)}
        onCancel={() => setPreview(null)}
        title={preview?.title}
        footer={null}
        width={1100}
        centered
      >
        {preview && (
          <iframe
            title={`Registre : ${preview.title}`}
            src={preview.url}
            className="h-[65vh] w-full rounded-lg border border-slate-200"
          />
        )}
      </Modal>
    </div>
  );
}
